/*
 * AgentService — Real-Implementation der CoreConsole-Schnittstelle.
 *
 * Haelt einen RealAgentTransport, sammelt cursorContext + reviewResults vor
 * jedem sendStreaming und reicht sie via context-Argument an den Transport.
 * Die Handler werden in das Format gemappt, das die CoreConsole heute
 * erwartet (onDelta/onDone/onError/...).
 *
 * Diese Klasse ersetzt die fruehere AnthropicChatService.
 */

import { AgentServiceBase } from './AgentServiceBase.js'
import { RealAgentTransport } from './RealAgentTransport.js'
import { AUTO_APPLY_TOOLS } from './autoApplyToolMap.js'
import { handleSessionLost } from '../auth/AuthService.js'

export class AgentService extends AgentServiceBase {
    constructor(serverUrl = '') {
        super()
        this.transport = new RealAgentTransport(serverUrl)
        this._keepAliveTimer = null
        this._onVisible = null            // visibilitychange-Listener (Keep-Alive)
        this._currentProjectId = null

        // Provider-Hooks (von main.js / CoreConsole gesetzt). Optional —
        // ohne sie schickt der Service nur die User-Message.
        this._cursorContextProvider = null   // () => cursorContext-Objekt
        this._reviewResultsProvider = null   // () => Array (wird nach Lesen geleert)

        // Legacy-Kompat: alter ChatService hat dieses Feld in CoreConsole gesetzt
        this._getEntries = null
    }

    /** Wird von CoreConsole gesetzt. */
    setCursorContextProvider(fn) { this._cursorContextProvider = fn }
    setReviewResultsProvider(fn) { this._reviewResultsProvider = fn }
    setEntriesProvider(fn) { this._getEntries = fn }

    async initialize() {
        this._startKeepAlive()
        return {
            text: 'Carbisdale Core connected. AI services available.',
            type: 'SYSTEM'
        }
    }

    async switchProject(projectId) {
        if (this._currentProjectId === projectId) return
        this._currentProjectId = projectId
        await this.transport.switchProject(projectId)
    }

    /** Fallback fuer nicht-streaming Aufrufe — sammelt deltas in einen String. */
    async send(message) {
        let result = { text: '', type: 'AI' }
        await this.sendStreaming(message, {
            onDelta: (text) => { result.text += text },
            onError: (err) => { result = { text: err.message, type: 'ERROR' } }
        })
        return result
    }

    /**
     * Streamt eine Nachricht vom Server. Sammelt cursorContext und
     * reviewResults aus den Providern (sofern gesetzt) und reicht sie via
     * context-Argument an den Transport.
     *
     * Die Callbacks haben das alte ChatService-Format:
     *   { onThinking(text), onThinkingDone(), onSearching(),
     *     onDelta(text), onDone(usage), onError(err) }
     * — sowie optional die neuen Agent-Handler:
     *   { onToolCall, onToolResult, onProposeRewrite, ..., onAborted, onRateLimit }
     */
    async sendStreaming(message, callbacks = {}) {
        const cursorContext = this._cursorContextProvider
            ? this._cursorContextProvider()
            : null
        const reviewResults = this._reviewResultsProvider
            ? this._reviewResultsProvider()
            : null

        const context = {
            projectId: this._currentProjectId,
            ...(cursorContext ? { cursorContext } : {}),
            ...(reviewResults && reviewResults.length > 0 ? { reviewResults } : {})
        }

        // Alte ChatService-Callbacks erwarten primitive args: onDelta(text),
        // onThinking(text). Die neuen Agent-Handler bekommen Objekte.
        const handlers = {
            onSession: callbacks.onSession,
            onSessionRecovered: callbacks.onSessionRecovered,
            onThinking: callbacks.onThinking
                ? (data) => callbacks.onThinking(data?.text || '')
                : undefined,
            onThinkingDone: callbacks.onThinkingDone,
            onSearching: callbacks.onSearching,
            onDelta: callbacks.onDelta
                ? (data) => callbacks.onDelta(data?.text || '')
                : undefined,
            onToolCall: callbacks.onToolCall,
            onToolResult: callbacks.onToolResult,
            // Review-pflichtige Vorschlaege (Haupttext)
            onProposeRewrite: callbacks.onProposeRewrite,
            onProposeInsert: callbacks.onProposeInsert,
            onProposeDelete: callbacks.onProposeDelete,
            // Auto-Apply (deklarativ aus AUTO_APPLY_TOOLS): Phase-3 Background-
            // Apply, Sketch, Note, Sections + Phase-4 UserMemory, ContextInfo,
            // JumpToSection. Neue Tools landen automatisch hier — ohne diesen
            // Pass-Through bleibt der `awaitActionDone`-Promise serverseitig
            // offen und das Tool haengt.
            ...Object.fromEntries(AUTO_APPLY_TOOLS.map(spec => {
                const key = `onPropose${spec.handlerName}`
                return [key, callbacks[key]]
            })),
            onIterationLimit: callbacks.onIterationLimit,
            onModelFallback: callbacks.onModelFallback,
            onRateLimit: callbacks.onRateLimit,
            onAborted: callbacks.onAborted,
            // Phase 4.5: await_review-Lifecycle. Console kann Status-UI
            // hochziehen; ReviewController prueft hasActiveAwaitReview()
            // vor jedem Submit, also kein eigener State-Spiegel hier.
            onAwaitReviewStarted: callbacks.onAwaitReviewStarted,
            onAwaitReviewDone: callbacks.onAwaitReviewDone,
            onError: callbacks.onError,
            onDone: callbacks.onDone
                ? (data) => callbacks.onDone(data?.usage || {})
                : undefined
        }

        await this.transport.sendMessage(message, context, handlers)
    }

    /**
     * Phase 3: Auto-Apply-Verdict zum Server posten (POST /api/agent/action-done).
     * Wird vom ReviewController gerufen, nachdem er ein propose_*-Auto-Apply-Event
     * verarbeitet und am Modell ausgefuehrt hat. Reicht direkt zum Transport durch.
     */
    async reportActionDone(info) {
        if (typeof this.transport.reportActionDone === 'function') {
            return this.transport.reportActionDone(info)
        }
    }

    /**
     * Phase 4.5: Review-Verdict-Buendel an einen offenen await_review-Waiter
     * schicken. Wird vom ReviewController NUR gerufen, wenn
     * `hasActiveAwaitReview()` true ist.
     */
    async reportReviewDone(reviewResults) {
        if (typeof this.transport.reportReviewDone === 'function') {
            return this.transport.reportReviewDone(reviewResults)
        }
        return null
    }

    /** @returns {boolean} ob ein await_review serverseitig aktiv ist */
    hasActiveAwaitReview() {
        return typeof this.transport.hasActiveAwaitReview === 'function'
            ? this.transport.hasActiveAwaitReview()
            : false
    }

    abort() {
        this.transport.abort()
    }

    destroy() {
        this._stopKeepAlive()
        this.transport.destroy()
    }

    _startKeepAlive() {
        this._stopKeepAlive()
        // Periodischer Ping haelt die Server-Session-TTL frisch, solange der Tab
        // im Vordergrund laeuft.
        this._keepAliveTimer = setInterval(() => this._pingSession(), 10 * 60 * 1000)
        // Zusatz-Ping beim Zurueckkehren in den Tab: Browser drosseln Timer im
        // Hintergrund stark und bei Laptop-Sleep stehen sie ganz. Sobald der Tab
        // wieder sichtbar wird, pingen wir sofort — so wird die TTL im
        // Reaktivierungs-Moment aufgefrischt, bevor der Autor den naechsten
        // Prompt sendet (statt erst beim naechsten 10-Min-Tick).
        if (typeof document !== 'undefined' && document.addEventListener) {
            this._onVisible = () => {
                if (document.visibilityState === 'visible') this._pingSession()
            }
            document.addEventListener('visibilitychange', this._onVisible)
        }
    }

    _stopKeepAlive() {
        if (this._keepAliveTimer) {
            clearInterval(this._keepAliveTimer)
            this._keepAliveTimer = null
        }
        if (this._onVisible && typeof document !== 'undefined' && document.removeEventListener) {
            document.removeEventListener('visibilitychange', this._onVisible)
            this._onVisible = null
        }
    }

    /**
     * Ein einzelner Keep-Alive-Ping. Reuse der bestehenden /api/session/ping-
     * Route — kein agent-eigenes Ping noetig, der sessionStore wird geteilt.
     * Wird vom Intervall-Timer und vom visibilitychange-Listener gerufen.
     */
    async _pingSession() {
        const projectId = this._currentProjectId
        const sid = projectId
            ? this.transport._sessionIdsByProject.get(projectId)
            : null
        if (!sid) return
        try {
            const res = await fetch(`${this.transport.serverUrl}/api/session/ping?id=${sid}`)
            // 401: JWT-Cookie abgelaufen — Ping stoppen UND Reload triggern,
            // damit der User zur Login-Seite kommt, ohne selbst F5 zu druecken.
            // 404 (Session-TTL abgelaufen) ignorieren wir bewusst: der naechste
            // sendMessage faengt das per Graceful-Recovery ab.
            if (res.status === 401) {
                this._stopKeepAlive()
                handleSessionLost()
            }
        } catch {
            // Netzwerk-Fehler (offline, Server-Restart) — Timer laufen lassen,
            // naechster Versuch kann gehen.
        }
    }
}
